import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Modal as RNModal, Platform, Pressable } from "react-native";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import type { VehicleDetection, SimilarFields } from "@visioni/a3s-vehicle-tracker";
import { Image } from "@src/components";
import { ZoomableImage, type ZoomableImageRef } from "@src/components/ZoomableImage";
import { useUnistyles, UnistylesRuntime } from "react-native-unistyles";
import { isKnown } from "./card-utils";

interface VehicleDetailPanelProps {
  vehicle: VehicleDetection;
  activeTimezone: string;
  actions: {
    onFindSimilar: (fields: SimilarFields) => void;
  };
}

/**
 * Expanded detail for a single detection.
 *
 * Deliberately lean: the make/model/colour/type live in the card title, and
 * the forensic attribute pills (bull bar, rack, tray, etc.), compass direction,
 * and full detection timestamp were dropped in favour of the AI-generated
 * appearanceDescription, which summarises them in prose.
 *
 * "Find similar" is a one-shot action (buildSimilarFields on the card) rather
 * than a field picker: the previous SimilarFieldsPicker let users choose which
 * fields — including forensic attributes — to match on, but that was removed
 * for a simpler flow. If per-field matching is needed again, reintroduce the
 * picker here (see git history) rather than adding fields to the one-shot path.
 */
export function VehicleDetailPanel({ vehicle }: VehicleDetailPanelProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [currentScale, setCurrentScale] = useState(1);
  const zoomRef = useRef<ZoomableImageRef>(null);

  const allImageUrls = useMemo(() => {
    const urls = [...vehicle.imageUrls];
    if (vehicle.plateImageKey) urls.push(vehicle.plateImageKey);
    return urls;
  }, [vehicle.imageUrls, vehicle.plateImageKey]);

  const expandedImageUrl = expandedIndex !== null ? (allImageUrls[expandedIndex] ?? null) : null;

  const goNext = useCallback(() => {
    setExpandedIndex((i) => (i !== null && i < allImageUrls.length - 1 ? i + 1 : i));
  }, [allImageUrls.length]);

  const goPrev = useCallback(() => {
    setExpandedIndex((i) => (i !== null && i > 0 ? i - 1 : i));
  }, []);

  const close = useCallback(() => {
    zoomRef.current?.resetZoom();
    setExpandedIndex(null);
  }, []);

  const handleImagePress = useCallback(
    (url: string) => {
      const idx = allImageUrls.indexOf(url);
      setCurrentScale(1);
      setExpandedIndex(idx >= 0 ? idx : 0);
    },
    [allImageUrls],
  );

  useEffect(() => {
    if (expandedIndex === null || Platform.OS !== "web") return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [expandedIndex, goNext, goPrev, close]);

  const plateConfidence = isKnown(vehicle.plateText) ? `${Math.round(vehicle.plateConfidence * 100)}%` : null;

  return (
    <View mt="3xl" gap="3xl">
      {/* Detail columns */}
      <DetailColumns vehicle={vehicle} plateConfidence={plateConfidence} />

      {/* Image gallery */}
      <ImageGallery
        imageKeys={vehicle.imageUrls}
        plateImageKey={vehicle.plateImageKey}
        onImagePress={handleImagePress}
      />

      {/* Fullscreen image modal */}
      <RNModal visible={!!expandedImageUrl} transparent animationType="fade" onRequestClose={close}>
        <Pressable
          onPress={(e) => {
            if (e.target === e.currentTarget) close();
          }}
          style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.9)", justifyContent: "center", alignItems: "center" }}
        >
          {expandedImageUrl && (
            <View style={{ width: "90%", height: "80%" }}>
              <ZoomableImage
                ref={zoomRef}
                source={{ uri: expandedImageUrl }}
                style={{ width: "100%", height: "100%" }}
                contentFit="scale-down"
                onScaleChange={setCurrentScale}
              />
            </View>
          )}
        </Pressable>

        {expandedIndex !== null && expandedIndex > 0 && (
          <Pressable
            onPress={goPrev}
            style={{
              position: "absolute",
              left: 16,
              top: "50%",
              marginTop: -20,
              width: 40,
              height: 40,
              borderRadius: 20,
              backgroundColor: "rgba(255,255,255,0.2)",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Icon name="chevronLeft" size={20} color="#fff" />
          </Pressable>
        )}

        {expandedIndex !== null && expandedIndex < allImageUrls.length - 1 && (
          <Pressable
            onPress={goNext}
            style={{
              position: "absolute",
              right: 16,
              top: "50%",
              marginTop: -20,
              width: 40,
              height: 40,
              borderRadius: 20,
              backgroundColor: "rgba(255,255,255,0.2)",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Icon name="chevronRight" size={20} color="#fff" />
          </Pressable>
        )}

        <Pressable
          onPress={close}
          style={{
            position: "absolute",
            top: 48,
            right: 24,
            width: 36,
            height: 36,
            borderRadius: 18,
            backgroundColor: "rgba(255,255,255,0.2)",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Text style={{ color: "#fff", fontSize: 18, fontWeight: "bold" }}>✕</Text>
        </Pressable>

        {/* Zoom level pill — tap to reset */}
        {currentScale > 1 && (
          <Pressable
            onPress={() => zoomRef.current?.resetZoom()}
            style={{
              position: "absolute",
              bottom: 32,
              alignSelf: "center",
              paddingHorizontal: 14,
              paddingVertical: 8,
              borderRadius: 16,
              backgroundColor: "rgba(0,0,0,0.6)",
              flexDirection: "row",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Text style={{ color: "#fff", fontSize: 13, fontWeight: "600" }}>{currentScale.toFixed(1)}x</Text>
            <Icon name="close" size={14} color="#fff" />
          </Pressable>
        )}
      </RNModal>
    </View>
  );
}

function DetailColumn({ label, value, flex = 1 }: { label: string; value: string; flex?: number }) {
  return (
    <View style={{ flex, minWidth: 100 }}>
      <Text textStyle="xs/semibold" color="text">
        {label}
      </Text>
      <Text textStyle="sm/regular" color="textMuted" numberOfLines={3}>
        {value}
      </Text>
    </View>
  );
}

function DetailColumns({ vehicle, plateConfidence }: { vehicle: VehicleDetection; plateConfidence: string | null }) {
  const { theme } = useUnistyles();
  const breakpoint = (UnistylesRuntime.breakpoint as string) ?? "md";
  const isSmall = breakpoint === "xs" || breakpoint === "sm";

  const columns: { key: string; label: string; value: string; flex?: number }[] = [];
  if (vehicle.appearanceDescription) {
    columns.push({ key: "desc", label: "Description", value: vehicle.appearanceDescription, flex: 2 });
  }
  if (vehicle.loadStatus) {
    columns.push({ key: "loadStatus", label: "Load Status", value: vehicle.loadStatus });
  }
  if (vehicle.loadContents) {
    columns.push({ key: "loadContents", label: "Load Content", value: vehicle.loadContents });
  }
  if (plateConfidence) {
    columns.push({ key: "plateConf", label: "Plate Confidence", value: plateConfidence });
  }

  if (columns.length === 0) return null;

  return (
    <View
      flexDirection={isSmall ? "column" : "row"}
      flexWrap="wrap"
      gap="md"
      alignItems={isSmall ? undefined : "stretch"}
    >
      {columns.map((col, i) => (
        <React.Fragment key={col.key}>
          {i > 0 && !isSmall && (
            <View
              style={{
                width: 1,
                backgroundColor: theme.colors.border,
                alignSelf: "stretch",
                marginHorizontal: theme.spacing.sm,
              }}
            />
          )}
          <DetailColumn label={col.label} value={col.value} flex={isSmall ? undefined : col.flex} />
        </React.Fragment>
      ))}
    </View>
  );
}

function ImageGallery({
  imageKeys,
  plateImageKey,
  onImagePress,
}: {
  imageKeys: string[];
  plateImageKey: string | null;
  onImagePress: (url: string) => void;
}) {
  if (imageKeys.length === 0 && !plateImageKey) return null;

  return (
    <View flexDirection="row" flexWrap="wrap" gap="sm">
      {imageKeys.map((url) => (
        <Pressable key={url} onPress={() => onImagePress(url)}>
          <View style={{ position: "relative" }}>
            <Image source={{ uri: url }} style={{ width: 210, height: 140, borderRadius: 11 }} contentFit="cover" />
            <View
              style={{
                position: "absolute",
                top: 4,
                right: 4,
                backgroundColor: "rgba(0,0,0,0.5)",
                borderRadius: 4,
                padding: 2,
              }}
            >
              <Icon name="fullscreen" size={14} color="#fff" />
            </View>
          </View>
        </Pressable>
      ))}
      {plateImageKey ?
        <Pressable onPress={() => onImagePress(plateImageKey)}>
          <View style={{ position: "relative" }}>
            <Image
              source={{ uri: plateImageKey }}
              style={{ width: 210, height: 140, borderRadius: 11 }}
              contentFit="cover"
            />
            <View
              style={{
                position: "absolute",
                top: 4,
                right: 4,
                backgroundColor: "rgba(0,0,0,0.5)",
                borderRadius: 4,
                padding: 2,
              }}
            >
              <Icon name="fullscreen" size={14} color="#fff" />
            </View>
          </View>
        </Pressable>
      : null}
    </View>
  );
}
